import { type GetTagSemanticiTipoSezione } from '@framework/api-client/api/users/schemas/getTagSemanticiTipoSezione.ts';
import { useGetTagSemantici } from '@framework/api-client/api/users/tag-semantici-controller/tag-semantici-controller.ts';
import { type SearchableField } from '@framework/ui/components/search/ISearchableField.tsx';
import { useMemo } from 'react';
import { mapSemanticTagsToSearchableFields } from '../mapSemanticTagsToSearchableFields.ts';
import { useSemanticTagSchemaShape } from './useSemanticTagSchemaShape.tsx';
import { useSemanticTagSearchableTransform } from './useSemanticTagSearchableTransform.ts';

/**
 * Hook che racchiude tutte le logiche per aggiungere le ricerche sui tag semantici
 * alle pagina di lista (procedimenti, richieste, ...), come i campi di ricerca,
 * il validatore e la trasformazione dei dati.
 */
export function useSemanticTagSerchableExtension(options: {
  /**
   * Filtro utilizzato per filtrare i tag semantici da utilizzare.
   * e.g. Frontoffice sono da vedere solo quelli FO, Backoffice invece entrambi.
   */
  sectionType?: GetTagSemanticiTipoSezione;
}) {
  const { sectionType } = options;

  // Recupero i tag semantici da BE, filtrandoli per tipoSezione se richiesto
  const semanticTagsResult = useGetTagSemantici({
    ordinamenti: ['nome'],
    tipoSezione: sectionType,
  });

  const semanticTags = useMemo(
    () => semanticTagsResult.data?.dati ?? [],
    [semanticTagsResult.data?.dati],
  );

  // Elenco di tutti i tag semantici _ricercabili_
  const searchableSemanticTags = useMemo(() => {
    return (
      semanticTagsResult.data?.dati?.filter(tag => tag.flRicercabile) ?? []
    );
  }, [semanticTagsResult.data?.dati]);

  // Di tag semantici ricercabili, costruisco i searchable da passare all'advancedSearch
  const searchableFields: SearchableField[] = useMemo(() => {
    return mapSemanticTagsToSearchableFields(searchableSemanticTags);
  }, [searchableSemanticTags]);

  // Costruisco lo shape dello schema per la ricerca avanzata
  const schemaShape = useSemanticTagSchemaShape({ searchableFields });

  // Funzione di transform necessaria per l'advancedSearch per gestire il passaggio
  // da oggetto (utilizzato internamente) a stringa (utilizzata per la ricerca in query string)
  const transform = useSemanticTagSearchableTransform(searchableSemanticTags);

  return {
    searchableSemanticTags,
    searchableFields,
    schemaShape,
    semanticTags,
    transform,
  };
}
